import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';
import './ExamReportCards.css';

function ExamReportCards() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [examMarks, setExamMarks] = useState([]);
  const [loadingMarks, setLoadingMarks] = useState(false);
  const [students, setStudents] = useState([]);
  const [exams, setExams] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [classes, setClasses] = useState([]);
  const [filteredMarks, setFilteredMarks] = useState([]);
  const [selectedMarkDetails, setSelectedMarkDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showResultCard, setShowResultCard] = useState(false);
  const [resultCardData, setResultCardData] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [publishExamName, setPublishExamName] = useState('');

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (!storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(storedSchoolData));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchClasses();
      fetchStudents(); // Fetch students first
      fetchExams();
      fetchTeachers();
    }
  }, [schoolData?.id, selectedBranchId]);

  // Fetch exam marks after students are loaded
  useEffect(() => {
    if (schoolData?.id && students.length > 0) {
      fetchExamMarks();
    }
  }, [schoolData?.id, students]);

  useEffect(() => {
    filterMarks();
  }, [selectedClass, selectedSection, selectedExam, examMarks]);

  useEffect(() => {
    setSelectedClass('');
    setSelectedSection('');
    setSelectedExam('');
    setExamMarks([]);
    setFilteredMarks([]);
  }, [selectedBranchId]);

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('class_name', { ascending: true });

      if (error) throw error;
      setClasses(data || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchExamMarks = async () => {
    try {
      setLoadingMarks(true);
      console.log('=== FETCHING EXAM MARKS ===');
      console.log('School ID:', schoolData.id);
      
      // Fetch all exam marks for this school
      let marksQuery = supabase
        .from('exam_marks')
        .select(`
          id,
          school_id,
          exam_schedule_id,
          student_id,
          teacher_id,
          subject_name,
          class_id,
          section_name,
          obtained_marks,
          total_marks,
          grade,
          percentage,
          remarks,
          is_active,
          created_at,
          updated_at,
          created_by,
          updated_by
        `)
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        marksQuery = marksQuery.eq('branch_id', selectedBranchId);
      }

      const { data: marksData, error: marksError } = await marksQuery.order('created_at', { ascending: false });

      if (marksError) {
        console.error('❌ Error fetching exam marks:', marksError);
        console.error('Error code:', marksError.code);
        console.error('Error message:', marksError.message);
        throw marksError;
      }

      console.log('✅ Raw marks data fetched:', marksData);
      console.log('Number of marks:', marksData?.length || 0);

      if (!marksData || marksData.length === 0) {
        console.log('⚠️ No marks found in database for school:', schoolData.id);
        setExamMarks([]);
        setLoadingMarks(false);
        return;
      }

      // Extract unique IDs
      const examIds = [...new Set(marksData.map(m => m.exam_schedule_id).filter(Boolean))];
      const studentIds = [...new Set(marksData.map(m => m.student_id).filter(Boolean))];
      const teacherIds = [...new Set(marksData.map(m => m.teacher_id).filter(Boolean))];

      console.log('Exam IDs to fetch:', examIds);
      console.log('Student IDs to fetch:', studentIds);
      console.log('Teacher IDs to fetch:', teacherIds);

      // Fetch exams
      let examsData = [];
      if (examIds.length > 0) {
        const { data: exams, error: examsError } = await supabase
          .from('exam_schedule')
          .select('id, title, type, start_date, end_date')
          .in('id', examIds);
        
        if (examsError) {
          console.error('Error fetching exams:', examsError);
        } else {
          examsData = exams || [];
          console.log('✅ Exams fetched:', examsData);
        }
      }

      // Use students from state (already fetched by fetchStudents function)
      // This avoids duplicate queries and the 400 error
      const studentsData = students || [];
      console.log(`✅ Using ${studentsData.length} students from state for matching marks`);
      
      if (studentsData.length === 0) {
        console.warn('⚠️ No students in state! Make sure fetchStudents() completed first.');
      }

      // Fetch teachers
      let teachersData = [];
      if (teacherIds.length > 0) {
        const { data: teachers, error: teachersError } = await supabase
          .from('teachers')
          .select('id, first_name, last_name')
          .in('id', teacherIds);
        
        if (teachersError) {
          console.error('Error fetching teachers:', teachersError);
        } else {
          teachersData = teachers || [];
          console.log('✅ Teachers fetched:', teachersData);
        }
      }

      // Combine the data - match the structure shown in Supabase
      const combinedData = marksData.map(mark => {
        const exam = examsData.find(e => e.id === mark.exam_schedule_id);
        // Find student from state (students are already loaded)
        const student = students.find(s => s.id === mark.student_id);
        const teacher = teachersData.find(t => t.id === mark.teacher_id);
        
        if (!student && mark.student_id) {
          console.log(`⚠️ Student ID ${mark.student_id} not found. Available IDs:`, students.map(s => s.id));
        } else if (student) {
          console.log(`✅ Matched student ${mark.student_id}: ${student.first_name} ${student.last_name}`);
        }
        
        // Ensure student data is available - use mark data as fallback
        const studentInfo = student || {
          id: mark.student_id,
          first_name: 'Unknown',
          last_name: 'Student',
          student_id: mark.student_id,
          class_id: mark.class_id,
          section_id: mark.section_name
        };
        
        return {
          ...mark,
          exam_schedule: exam || null,
          exam_schedule_id: mark.exam_schedule_id,
          students: studentInfo,
          student_id: mark.student_id,
          teachers: teacher || null,
          teacher_id: mark.teacher_id,
          // Ensure all fields are present
          subject_name: mark.subject_name,
          class_id: mark.class_id,
          section_name: mark.section_name,
          obtained_marks: mark.obtained_marks,
          total_marks: mark.total_marks,
          grade: mark.grade,
          percentage: mark.percentage,
          remarks: mark.remarks,
          is_active: mark.is_active
        };
      });
      
      // Update students state for use in grouping
      if (studentsData && studentsData.length > 0) {
        setStudents(studentsData);
        console.log('✅ Students state updated:', studentsData.length, 'students');
      }

      console.log('✅ Combined marks data:', combinedData);
      console.log('=== FETCH COMPLETE ===');
      setExamMarks(combinedData);
    } catch (error) {
      console.error('❌ Error fetching exam marks:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      alert(`Error loading exam marks: ${error.message || 'Unknown error'}\n\nCheck browser console (F12) for details.`);
      setExamMarks([]);
    } finally {
      setLoadingMarks(false);
    }
  };

  const fetchStudents = async () => {
    try {
      let query = supabase
        .from('students')
        .select('id, first_name, last_name, middle_name, student_id_no, class_id, section_id')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('first_name');

      if (error) throw error;
      // Map student_id_no to student_id for consistency
      const mappedStudents = (data || []).map(student => ({
        ...student,
        student_id: student.student_id_no // Add student_id alias
      }));
      console.log(`✅ Fetched ${mappedStudents.length} students`);
      setStudents(mappedStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
    }
  };

  const fetchExams = async () => {
    try {
      let query = supabase
        .from('exam_schedule')
        .select('id, title, type, start_date, end_date')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('start_date', { ascending: false });

      if (error) throw error;
      setExams(data || []);
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchTeachers = async () => {
    try {
      let query = supabase
        .from('teachers')
        .select('id, first_name, last_name')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTeachers(data || []);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const handlePublishClick = () => {
    if (!selectedExam) {
      alert('Please select an exam/test first');
      return;
    }
    const examName = exams.find(e => e.id === parseInt(selectedExam))?.title || 'this exam';
    setPublishExamName(examName);
    setShowPublishConfirm(true);
  };

  const publishExamResults = async () => {
    setShowPublishConfirm(false);
    
    try {
      setPublishing(true);
      
      // Update exam_schedule to mark as published
      const { error: examError } = await supabase
        .from('exam_schedule')
        .update({ is_published: true, published_at: new Date().toISOString() })
        .eq('id', parseInt(selectedExam))
        .eq('school_id', schoolData.id);

      if (examError) throw examError;

      // Update all exam marks for this exam to published
      const { error: marksError } = await supabase
        .from('exam_marks')
        .update({ is_published: true, published_at: new Date().toISOString() })
        .eq('exam_schedule_id', parseInt(selectedExam))
        .eq('school_id', schoolData.id);

      if (marksError) throw marksError;

      // Show success popup
      setShowPublishSuccess(true);
      
      // Refresh the data
      fetchExamMarks();
      fetchExams();
    } catch (error) {
      console.error('Error publishing exam results:', error);
      alert(`❌ Error publishing results: ${error.message || 'Unknown error'}`);
    } finally {
      setPublishing(false);
    }
  };

  const filterMarks = () => {
    let filtered = [...examMarks];

    if (selectedClass) {
      filtered = filtered.filter(mark => mark.class_id === parseInt(selectedClass));
    }

    if (selectedSection) {
      filtered = filtered.filter(mark => 
        mark.section_name === selectedSection || 
        mark.section_name === selectedSection.toString()
      );
    }

    if (selectedExam) {
      filtered = filtered.filter(mark => 
        mark.exam_schedule_id === parseInt(selectedExam)
      );
    }

    setFilteredMarks(filtered);
  };

  const getStudentName = (student) => {
    if (!student) return 'N/A';
    return `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.trim();
  };

  const getTeacherName = (teacher) => {
    if (!teacher) return 'N/A';
    return `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim();
  };

  const getExamName = (exam) => {
    if (!exam) return 'N/A';
    return exam.title || 'Untitled Exam';
  };

  // Group marks by student
  const groupedByStudent = filteredMarks.reduce((acc, mark) => {
    const studentId = mark.student_id;
    if (!acc[studentId]) {
      // Try multiple sources for student data
      let studentInfo = null;
      
      // 1. Try from students state
      if (students && students.length > 0) {
        studentInfo = students.find(s => s.id === studentId);
      }
      
      // 2. Try from mark.students
      if (!studentInfo && mark.students) {
        studentInfo = mark.students;
      }
      
      // 3. Create fallback with available data
      if (!studentInfo) {
        studentInfo = {
          id: mark.student_id,
          first_name: 'Unknown',
          last_name: 'Student',
          student_id: mark.student_id,
          class_id: mark.class_id,
          section_id: mark.section_name
        };
        console.log(`⚠️ Using fallback student for ID ${studentId}`);
      } else {
        console.log(`✅ Found student for ID ${studentId}:`, studentInfo);
      }
      
      acc[studentId] = {
        student: studentInfo,
        marks: []
      };
    }
    acc[studentId].marks.push(mark);
    return acc;
  }, {});

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      sessionStorage.setItem('schoolLoggedOut', 'true');
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      navigate('/schoollogin');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/schoollogin');
    }
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>📊 Report Cards - Exam Results</h1>
          <div className="user-info">
            <span>{schoolData?.school_name || 'School'}</span>
            <div className="user-avatar">🏫</div>
          </div>
        </div>

        <div className="content-body">
          <div className="exam-report-cards-container">
            {/* Filters */}
            <div className="report-cards-filters">
              <BranchSelector
                schoolId={schoolData?.id}
                selectedBranchId={selectedBranchId}
                onBranchChange={handleBranchChange}
              />
              <div className="filter-group">
                <label>Class:</label>
                <select 
                  value={selectedClass} 
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Classes</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.class_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Section:</label>
                <select 
                  value={selectedSection} 
                  onChange={(e) => setSelectedSection(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Sections</option>
                  {selectedClass && classes.find(c => c.id === parseInt(selectedClass))?.sections?.map((section, idx) => (
                    <option key={idx} value={section}>
                      {section}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Exam/Test:</label>
                <select 
                  value={selectedExam} 
                  onChange={(e) => setSelectedExam(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Exams</option>
                  {exams.map(exam => (
                    <option key={exam.id} value={exam.id}>
                      {exam.title} ({exam.type})
                    </option>
                  ))}
                </select>
              </div>

              {selectedExam && (
                <button
                  onClick={handlePublishClick}
                  disabled={publishing}
                  className="publish-btn"
                  title="Publish results for this exam/test"
                >
                  {publishing ? '⏳ Publishing...' : '📢 Publish Results'}
                </button>
              )}
            </div>

            {loadingMarks ? (
              <div className="loading-container">
                <div className="loading-spinner">⏳</div>
                <p>Loading exam marks...</p>
              </div>
            ) : filteredMarks.length === 0 ? (
              <div className="no-data">
                <div className="no-data-icon">📋</div>
                <h3>No Exam Marks Found</h3>
                <p>
                  {selectedClass || selectedSection || selectedExam 
                    ? 'No marks found for the selected filters. Try different filters.'
                    : 'No exam marks have been uploaded by teachers yet.'}
                </p>
              </div>
            ) : (
              <div className="report-cards-list">
                {Object.entries(groupedByStudent).map(([studentId, data]) => {
                  // Get marks for selected exam if one is selected
                  const examMarksForStudent = selectedExam 
                    ? data.marks.filter(m => m.exam_schedule_id === parseInt(selectedExam))
                    : [];
                  
                  return (
                    <div key={studentId} className="student-report-card">
                    <div className="student-header">
                      <div className="student-info">
                        <h3>
                          {(() => {
                            const name = getStudentName(data.student);
                            if (name && name !== 'N/A' && name.trim() !== '' && name !== 'Unknown Student') {
                              return name;
                            }
                            // Fallback: show student ID
                            const studentId = data.student?.student_id || data.student?.id || data.marks[0]?.student_id;
                            return studentId ? `Student ID: ${studentId}` : 'Unknown Student';
                          })()}
                        </h3>
                        <p className="student-id">
                          ID: {data.student?.student_id || data.student?.id || data.marks[0]?.student_id || 'N/A'}
                        </p>
                        <p className="student-class">
                          Class: {
                            (() => {
                              const classId = data.student?.class_id || data.marks[0]?.class_id;
                              if (classId) {
                                const className = classes.find(c => c.id === classId)?.class_name;
                                return className || `Class ${classId}`;
                              }
                              return 'N/A';
                            })()
                          } - 
                          Section: {
                            data.student?.section_id || 
                            data.marks[0]?.section_name || 
                            'N/A'
                          }
                        </p>
                        {selectedExam && examMarksForStudent.length > 0 && (
                          <button
                            onClick={() => {
                              const selectedExamData = exams.find(e => e.id === parseInt(selectedExam));
                              setResultCardData({
                                student: data.student,
                                exam: selectedExamData,
                                marks: examMarksForStudent,
                                class: classes.find(c => c.id === (data.student?.class_id || data.marks[0]?.class_id))
                              });
                              setShowResultCard(true);
                            }}
                            className="view-result-card-btn"
                          >
                            📋 View Result Card
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="marks-table-container">
                      <table className="marks-table">
                        <thead>
                          <tr>
                            <th>Exam/Test</th>
                            <th>Subject</th>
                            <th>Obtained Marks</th>
                            <th>Total Marks</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.marks.map((mark, idx) => {
                            return (
                              <tr key={idx}>
                                <td>
                                  <div className="exam-info">
                                    <strong>{getExamName(mark.exam_schedule)}</strong>
                                    <span className="exam-type">
                                      {mark.exam_schedule?.type === 'exam' ? 'Exam' : 
                                       mark.exam_schedule?.type === 'monthly_test' ? 'Monthly Test' : 
                                       mark.exam_schedule?.type === 'holiday' ? 'Holiday' : 'Test'}
                                    </span>
                                  </div>
                                </td>
                                <td style={{ fontWeight: 600, color: '#1F2937' }}>
                                  {mark.subject_name || 'N/A'}
                                </td>
                                <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                  {mark.obtained_marks !== null && mark.obtained_marks !== undefined 
                                    ? parseFloat(mark.obtained_marks).toFixed(2)
                                    : '-'}
                                </td>
                                <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                  {mark.total_marks !== null && mark.total_marks !== undefined 
                                    ? parseFloat(mark.total_marks).toFixed(2)
                                    : '-'}
                                </td>
                                <td>
                                  <button
                                    onClick={() => {
                                      setSelectedMarkDetails(mark);
                                      setShowDetailsModal(true);
                                    }}
                                    className="view-details-btn"
                                    title="View Details"
                                  >
                                    👁️ View Details
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedMarkDetails && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Exam Details</h2>
              <button className="modal-close-btn" onClick={() => setShowDetailsModal(false)}>
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h3>Student Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Student Name:</span>
                    <span className="detail-value">{getStudentName(selectedMarkDetails.students)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Student ID:</span>
                    <span className="detail-value">{selectedMarkDetails.students?.student_id || selectedMarkDetails.student_id || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Class:</span>
                    <span className="detail-value">
                      {classes.find(c => c.id === selectedMarkDetails.class_id)?.class_name || selectedMarkDetails.class_id || 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Section:</span>
                    <span className="detail-value">{selectedMarkDetails.section_name || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Exam Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Exam/Test:</span>
                    <span className="detail-value">{getExamName(selectedMarkDetails.exam_schedule)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Type:</span>
                    <span className="detail-value">
                      {selectedMarkDetails.exam_schedule?.type === 'exam' ? 'Exam' : 
                       selectedMarkDetails.exam_schedule?.type === 'monthly_test' ? 'Monthly Test' : 
                       selectedMarkDetails.exam_schedule?.type === 'holiday' ? 'Holiday' : 'Test'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Subject:</span>
                    <span className="detail-value">{selectedMarkDetails.subject_name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Teacher:</span>
                    <span className="detail-value">{getTeacherName(selectedMarkDetails.teachers)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Marks & Grades</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Obtained Marks:</span>
                    <span className="detail-value">
                      {selectedMarkDetails.obtained_marks !== null && selectedMarkDetails.obtained_marks !== undefined 
                        ? parseFloat(selectedMarkDetails.obtained_marks).toFixed(2)
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Total Marks:</span>
                    <span className="detail-value">
                      {selectedMarkDetails.total_marks !== null && selectedMarkDetails.total_marks !== undefined 
                        ? parseFloat(selectedMarkDetails.total_marks).toFixed(2)
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Percentage:</span>
                    <span className="detail-value">
                      {selectedMarkDetails.percentage !== null && selectedMarkDetails.percentage !== undefined 
                        ? `${parseFloat(selectedMarkDetails.percentage).toFixed(2)}%`
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Grade:</span>
                    <span className="detail-value">
                      {selectedMarkDetails.grade ? (
                        <span className="grade-badge">{selectedMarkDetails.grade}</span>
                      ) : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {selectedMarkDetails.remarks && (
                <div className="detail-section">
                  <h3>Remarks</h3>
                  <p className="remarks-text">{selectedMarkDetails.remarks}</p>
                </div>
              )}

              <div className="detail-section">
                <h3>Additional Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Date Added:</span>
                    <span className="detail-value">
                      {selectedMarkDetails.created_at 
                        ? new Date(selectedMarkDetails.created_at).toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Last Updated:</span>
                    <span className="detail-value">
                      {selectedMarkDetails.updated_at 
                        ? new Date(selectedMarkDetails.updated_at).toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="modal-close-button" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Card Modal */}
      {showResultCard && resultCardData && (
        <div className="modal-overlay" onClick={() => setShowResultCard(false)}>
          <div className="result-card-modal" onClick={(e) => e.stopPropagation()}>
            <div className="result-card-header">
              <div className="result-card-school-info">
                <h1>REPORT CARD</h1>
                <p className="school-name">{schoolData?.school_name || 'School Name'}</p>
              </div>
              <button className="modal-close-btn" onClick={() => setShowResultCard(false)}>
                ✕
              </button>
            </div>

            <div className="result-card-body">
              {/* Student Info Section */}
              <div className="result-card-student-info">
                <div className="student-info-row">
                  <div className="info-item">
                    <span className="info-label">Student Name:</span>
                    <span className="info-value">{getStudentName(resultCardData.student)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Student ID:</span>
                    <span className="info-value">{resultCardData.student?.student_id || resultCardData.student?.id || 'N/A'}</span>
                  </div>
                </div>
                <div className="student-info-row">
                  <div className="info-item">
                    <span className="info-label">Class:</span>
                    <span className="info-value">{resultCardData.class?.class_name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Section:</span>
                    <span className="info-value">{resultCardData.student?.section_id || resultCardData.marks[0]?.section_name || 'N/A'}</span>
                  </div>
                </div>
                <div className="student-info-row">
                  <div className="info-item">
                    <span className="info-label">Exam/Test:</span>
                    <span className="info-value">{getExamName(resultCardData.exam)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Type:</span>
                    <span className="info-value">
                      {resultCardData.exam?.type === 'exam' ? 'Exam' : 
                       resultCardData.exam?.type === 'monthly_test' ? 'Monthly Test' : 
                       resultCardData.exam?.type === 'holiday' ? 'Holiday' : 'Test'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Marks Table */}
              <div className="result-card-marks-table">
                <table className="result-card-table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Obtained Marks</th>
                      <th>Total Marks</th>
                      <th>Percentage</th>
                      <th>Grade</th>
                      <th>Teacher</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultCardData.marks.map((mark, idx) => (
                      <tr key={idx}>
                        <td className="subject-cell">
                          <strong>{mark.subject_name}</strong>
                        </td>
                        <td className="marks-cell">
                          {mark.obtained_marks !== null && mark.obtained_marks !== undefined 
                            ? parseFloat(mark.obtained_marks).toFixed(2)
                            : '-'}
                        </td>
                        <td className="marks-cell">
                          {mark.total_marks !== null && mark.total_marks !== undefined 
                            ? parseFloat(mark.total_marks).toFixed(2)
                            : '-'}
                        </td>
                        <td className="percentage-cell">
                          {mark.percentage !== null && mark.percentage !== undefined 
                            ? `${parseFloat(mark.percentage).toFixed(2)}%`
                            : '-'}
                        </td>
                        <td className="grade-cell">
                          {mark.grade ? (
                            <span className="grade-badge-large">{mark.grade}</span>
                          ) : '-'}
                        </td>
                        <td className="teacher-cell">
                          {getTeacherName(mark.teachers)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>
                        <strong>
                          {resultCardData.marks.reduce((sum, m) => {
                            const obtained = parseFloat(m.obtained_marks) || 0;
                            return sum + obtained;
                          }, 0).toFixed(2)}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {resultCardData.marks.reduce((sum, m) => {
                            const total = parseFloat(m.total_marks) || 0;
                            return sum + total;
                          }, 0).toFixed(2)}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {(() => {
                            const totalObtained = resultCardData.marks.reduce((sum, m) => {
                              return sum + (parseFloat(m.obtained_marks) || 0);
                            }, 0);
                            const totalMarks = resultCardData.marks.reduce((sum, m) => {
                              return sum + (parseFloat(m.total_marks) || 0);
                            }, 0);
                            return totalMarks > 0 ? ((totalObtained / totalMarks) * 100).toFixed(2) : '0.00';
                          })()}%
                        </strong>
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Remarks Section */}
              {resultCardData.marks.some(m => m.remarks) && (
                <div className="result-card-remarks">
                  <h3>Remarks</h3>
                  <div className="remarks-list">
                    {resultCardData.marks.filter(m => m.remarks).map((mark, idx) => (
                      <div key={idx} className="remark-item">
                        <strong>{mark.subject_name}:</strong> {mark.remarks}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="result-card-footer">
              <button className="modal-close-button" onClick={() => setShowResultCard(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {showPublishConfirm && (
        <div className="modal-overlay" onClick={() => setShowPublishConfirm(false)}>
          <div className="publish-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="publish-confirm-header">
              <div className="publish-confirm-icon">📢</div>
              <h2>Publish Exam Results</h2>
            </div>
            
            <div className="publish-confirm-body">
              <p className="publish-confirm-text">
                Are you sure you want to publish the results for
              </p>
              <p className="publish-exam-name">
                <strong>"{publishExamName}"</strong>?
              </p>
              <p className="publish-confirm-warning">
                ⚠️ Once published, students and parents will be able to view these results.
              </p>
            </div>

            <div className="publish-confirm-footer">
              <button 
                className="publish-cancel-btn" 
                onClick={() => setShowPublishConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="publish-confirm-btn" 
                onClick={publishExamResults}
                disabled={publishing}
              >
                {publishing ? '⏳ Publishing...' : 'Yes, Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Success Modal */}
      {showPublishSuccess && (
        <div className="modal-overlay" onClick={() => setShowPublishSuccess(false)}>
          <div className="publish-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="publish-success-header">
              <div className="publish-success-icon">✅</div>
              <h2>Results Published Successfully!</h2>
            </div>
            
            <div className="publish-success-body">
              <p className="publish-success-text">
                The results for <strong>"{publishExamName}"</strong> have been published successfully.
              </p>
              <p className="publish-success-info">
                Students and parents can now view these results in their dashboards.
              </p>
            </div>

            <div className="publish-success-footer">
              <button 
                className="publish-success-btn" 
                onClick={() => setShowPublishSuccess(false)}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExamReportCards;

